// app/javascript/controllers/flyout_controller.js
//
// Generic hover/focus/tap flyout (popover). Content-agnostic — knows nothing about
// what it's showing. It owns behavior only: open/close/pin, portal-to-<body>
// (position:fixed, so it escapes any overflow/stacking context), positioning + flip,
// keyboard, and a11y wiring. The card chrome + inner content come from the partial's
// <template>; this controller clones that template and positions the clone.
//
// Register:
//   import FlyoutController from "./flyout_controller"
//   application.register("flyout", FlyoutController)

import { Controller } from "@hotwired/stimulus";

const CLOSE_DELAY = 140;
let uid = 0;

export default class extends Controller {
  static targets = ["trigger", "template"];
  static values = { triggerId: String, showDelay: { type: Number, default: 120 } };

  get triggerElement() { return this.externalTrigger || this.triggerTarget; }

  connect() {
    this.flyout = null;
    this.showTimer = null;
    this.hideTimer = null;
    this.pinned = false;
    this.id = `flyout-${uid++}`;
    this._reposition = () => { this.anchor = null; this.position(); };
    this._onDocClick = (e) => this.onDocClick(e);
    this.externalTrigger = this.triggerIdValue ? document.getElementById(this.triggerIdValue) : null;
    if (this.externalTrigger) {
      this.externalListeners = {
        mouseenter: (event) => { this.anchor = { left: event.clientX, top: event.clientY, bottom: event.clientY, width: 0 }; this.scheduleShow(); },
        mouseleave: () => this.scheduleHide(),
        focus: () => { this.anchor = null; this.show(); },
        blur: () => this.hide(),
        keydown: (event) => this.onKeydown(event),
        pointerdown: () => this.hide()
      };
      Object.entries(this.externalListeners).forEach(([name, listener]) => this.externalTrigger.addEventListener(name, listener));
    }
  }

  disconnect() {
    this.cancelShow();
    this.cancelHide();
    this.teardownGlobalListeners();
    if (this.externalTrigger) {
      Object.entries(this.externalListeners).forEach(([name, listener]) => this.externalTrigger.removeEventListener(name, listener));
      this.externalTrigger.removeAttribute("aria-describedby");
    }
    if (this.flyout) { this.flyout.remove(); this.flyout = null; }
  }

  // --- actions (wired via data-action in the partial) ---
  scheduleShow() { this.cancelHide(); this.cancelShow(); if (this.pinned) return; if (this.showDelayValue === 0) this.show(); else this.showTimer = setTimeout(() => this.show(), this.showDelayValue); }
  scheduleHide() { this.cancelShow(); if (this.pinned) return; this.hideTimer = setTimeout(() => this.hide(), CLOSE_DELAY); }

  show() {
    this.cancelShow();
    this.cancelHide();
    if (!this.externalTrigger && !this.hasTriggerTarget) return;
    this.ensureFlyout();
    this.position();
    this.flyout.dataset.open = "true";
    this.flyout.setAttribute("aria-hidden", "false");
    this.triggerElement.setAttribute("aria-describedby", this.id);
    window.addEventListener("scroll", this._reposition, true);
    window.addEventListener("resize", this._reposition);
  }

  hide() {
    this.cancelShow();
    if (!this.flyout) return;
    this.flyout.dataset.open = "false";
    this.flyout.setAttribute("aria-hidden", "true");
    this.pinned = false;
    this.triggerElement.removeAttribute("aria-describedby");
    this.teardownGlobalListeners();
  }

  toggle(event) {
    if (event) event.preventDefault();
    if (this.pinned) { this.hide(); return; }
    this.pinned = true;
    this.show();
    setTimeout(() => document.addEventListener("click", this._onDocClick, true), 0);
  }

  onKeydown(event) {
    if (event.key === "Escape") { this.hide(); this.triggerElement.blur(); }
  }

  blurHide() { if (!this.pinned) this.hide(); }

  // --- internals ---
  cancelShow() { clearTimeout(this.showTimer); }
  cancelHide() { clearTimeout(this.hideTimer); }

  onDocClick(e) {
    if (this.triggerElement.contains(e.target) || (this.flyout && this.flyout.contains(e.target))) return;
    this.hide();
  }

  ensureFlyout() {
    if (this.flyout) return;
    const node = this.templateTarget.content.firstElementChild.cloneNode(true);
    node.id = this.id;
    node.addEventListener("mouseenter", () => this.cancelHide());
    node.addEventListener("mouseleave", () => this.scheduleHide());
    document.body.appendChild(node);
    this.flyout = node;
  }

  position() {
    const f = this.flyout;
    if (!f) return;
    const r = this.anchor || this.triggerElement.getBoundingClientRect();
    const fw = f.offsetWidth, fh = f.offsetHeight, M = 8, GAP = 10;
    const below = (window.innerHeight - r.bottom) >= (fh + GAP + M) || r.top < (fh + GAP + M);
    const top = below ? r.bottom + GAP : r.top - fh - GAP;
    let left = r.left + r.width / 2 - fw / 2;
    left = Math.max(M, Math.min(left, window.innerWidth - fw - M));
    f.style.top = `${Math.round(Math.max(M, Math.min(top, window.innerHeight - fh - M)))}px`;
    f.style.left = `${Math.round(left)}px`;
    f.dataset.placement = below ? "below" : "above";
    const center = r.left + r.width / 2;
    const arrowLeft = Math.max(14, Math.min(center - left, fw - 14));
    f.style.setProperty("--flyout-arrow-left", `${Math.round(arrowLeft)}px`);
  }

  teardownGlobalListeners() {
    window.removeEventListener("scroll", this._reposition, true);
    window.removeEventListener("resize", this._reposition);
    document.removeEventListener("click", this._onDocClick, true);
  }
}
